import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Loader2, Plus, Search, Package, Users, Calendar, MapPin, 
  FileText, CheckCircle, Clock, AlertTriangle, Play, RotateCcw,
  ChevronDown, ChevronUp, Trash2, Edit, Eye, X, Building2, Upload, CalendarDays, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import api, { osApi, estoqueApi, humanApi } from '../lib/api';

const statusConfig = {
  aberta: { label: 'Aberta', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  executada: { label: 'Executada', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  fechada: { label: 'Fechada', color: 'bg-stone-500/20 text-stone-400 border-stone-500/30' },
  cancelada: { label: 'Cancelada', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const OsGestaoPage = () => {
  const [loading, setLoading] = useState(false);
  const [ordens, setOrdens] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOs, setExpandedOs] = useState(null);
  
  // Modal criar/editar OS
  const [showOsModal, setShowOsModal] = useState(false);
  const [editingOs, setEditingOs] = useState(null);
  const [osForm, setOsForm] = useState({
    numero: '', cliente: '', local: '', servicos: '',
    data_evento: '', data_evento_fim: '',
    data_montagem: '',
    horario_inicio_montagem: '', horario_fim_montagem: '',
    horario_inicio: '', horario_fim: '',
    prazo_execucao: '', observacoes: '',
    equipe: [], insumos_previstos: []
  });
  
  // Modal executar OS
  const [showExecutarModal, setShowExecutarModal] = useState(false);
  const [executandoOs, setExecutandoOs] = useState(null);
  const [insumosAjustados, setInsumosAjustados] = useState([]);
  const [executando, setExecutando] = useState(false);
  
  // Modal confirmar reversao
  const [showReverterDialog, setShowReverterDialog] = useState(false);
  const [revertendoOs, setRevertendoOs] = useState(null);
  
  // Modal importar PDF
  const [showImportarModal, setShowImportarModal] = useState(false);
  const [importandoPdf, setImportandoPdf] = useState(false);
  const [pdfDados, setPdfDados] = useState(null);
  const fileInputRef = useRef(null);
  
  // Escala de equipe
  const [addingMembro, setAddingMembro] = useState(null); // OS que está adicionando membro
  const [novoMembro, setNovoMembro] = useState({ pessoa_id: '', funcao: '', horario_inicio: '', horario_fim: '' });

  // Carregar dados
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [osRes, prodRes, locRes, funcRes] = await Promise.all([
        osApi.list(),
        estoqueApi.listarProdutos(),
        estoqueApi.listarLocais(),
        humanApi.list()
      ]);
      setOrdens(osRes.data || []);
      setProdutos(prodRes.data || []);
      setLocais(locRes.data || []);
      setFuncionarios(funcRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar ordens
  const ordensFiltradas = ordens.filter(os => {
    const matchSearch = !searchTerm || 
      os.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      os.cliente?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || os.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Abrir modal para nova OS
  const handleNovaOs = () => {
    setEditingOs(null);
    setOsForm({
      numero: '', cliente: '', local: '', servicos: '',
      data_evento: '', data_evento_fim: '',
      data_montagem: '',
      horario_inicio_montagem: '', horario_fim_montagem: '',
      horario_inicio: '', horario_fim: '',
      prazo_execucao: '', observacoes: '',
      equipe: [], insumos_previstos: []
    });
    setShowOsModal(true);
  };
  
  // Parsear PDF de OS
  const handleParsePdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Selecione um arquivo PDF');
      return;
    }
    
    setImportandoPdf(true);
    const formData = new FormData();
    formData.append('arquivo', file);
    
    try {
      const response = await api.post('/os/parse-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPdfDados(response.data);
      setShowImportarModal(true);
    } catch (error) {
      console.error('Erro ao parsear PDF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar PDF');
    } finally {
      setImportandoPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Importar OS do PDF
  const handleImportarOs = async () => {
    if (!pdfDados) return;
    
    setImportandoPdf(true);
    try {
      // Montar observações completas
      let obsCompleta = pdfDados.observacoes || '';
      
      // Adicionar serviços às observações
      if (pdfDados.servicos?.length > 0) {
        const servicosStr = pdfDados.servicos.map(s => `${s.descricao} (x${s.quantidade})`).join(', ');
        obsCompleta += (obsCompleta ? ' | ' : '') + 'SERVIÇOS: ' + servicosStr;
      }
      
      // Adicionar peças às observações
      if (pdfDados.pecas?.length > 0) {
        const pecasStr = pdfDados.pecas.map(p => `${p.descricao} (x${p.quantidade})`).join(', ');
        obsCompleta += (obsCompleta ? ' | ' : '') + 'PEÇAS/EQUIPAMENTOS: ' + pecasStr;
      }
      
      // Adicionar horário às observações
      if (pdfDados.horario_inicio || pdfDados.horario_fim) {
        obsCompleta += (obsCompleta ? ' | ' : '') + `HORÁRIO: ${pdfDados.horario_inicio || ''} às ${pdfDados.horario_fim || ''}`;
      }
      
      // Adicionar contato às observações
      if (pdfDados.contato || pdfDados.telefone) {
        obsCompleta += (obsCompleta ? ' | ' : '') + `CONTATO: ${pdfDados.contato || ''} ${pdfDados.telefone ? '(' + pdfDados.telefone + ')' : ''}`;
      }
      
      const osData = {
        numero: pdfDados.numero,
        cliente: pdfDados.cliente,
        local: pdfDados.local + (pdfDados.descricao_evento ? ' - ' + pdfDados.descricao_evento : ''),
        servicos: pdfDados.descricao_evento || pdfDados.local,
        data_evento: pdfDados.data_evento,
        data_evento_fim: pdfDados.data_evento_fim || null,
        periodo_completo: pdfDados.periodo_completo || null,
        data_montagem: pdfDados.data_montagem || null,
        prazo_execucao: null,
        observacoes: obsCompleta,
        status: 'aberta',
        equipe: [],
        insumos_previstos: []
      };
      
      await osApi.create(osData);
      toast.success(`OS ${pdfDados.numero} importada com sucesso!`);
      setShowImportarModal(false);
      setPdfDados(null);
      loadData();
    } catch (error) {
      console.error('Erro ao importar OS:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar OS');
    } finally {
      setImportandoPdf(false);
    }
  };

  // Abrir modal para editar OS
  const handleEditarOs = (os) => {
    setEditingOs(os);
    setOsForm({
      numero: os.numero || '',
      cliente: os.cliente || '',
      local: os.local || '',
      servicos: os.servicos || '',
      data_evento: os.data_evento || '',
      data_evento_fim: os.data_evento_fim || '',
      data_montagem: os.data_montagem || '',
      horario_inicio_montagem: os.horario_inicio_montagem || '',
      horario_fim_montagem: os.horario_fim_montagem || '',
      horario_inicio: os.horario_inicio || '',
      horario_fim: os.horario_fim || '',
      prazo_execucao: os.prazo_execucao || '',
      observacoes: os.observacoes || '',
      equipe: os.equipe || [],
      insumos_previstos: os.insumos_previstos || []
    });
    setShowOsModal(true);
  };

  // Salvar OS
  const handleSalvarOs = async () => {
    if (!osForm.numero || !osForm.cliente || !osForm.data_evento) {
      toast.error('Numero, cliente e data do evento sao obrigatorios');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...osForm,
        insumos_previstos: osForm.insumos_previstos.map(i => ({
          produto_id: i.produto_id,
          quantidade_prevista: parseFloat(i.quantidade_prevista),
          local_origem_id: i.local_origem_id
        })),
        equipe: osForm.equipe.map(e => ({
          human_id: e.human_id,
          funcao: e.funcao,
          fases: (e.fases && e.fases.length > 0) ? e.fases : ['montagem', 'evento', 'encerramento']
        }))
      };

      if (editingOs) {
        await osApi.update(editingOs.numero, payload);
        toast.success('OS atualizada!');
      } else {
        await osApi.create(payload);
        toast.success('OS criada!');
      }
      
      setShowOsModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar OS');
    } finally {
      setLoading(false);
    }
  };

  // Adicionar insumo previsto
  const handleAddInsumo = () => {
    setOsForm({
      ...osForm,
      insumos_previstos: [
        ...osForm.insumos_previstos,
        { produto_id: '', quantidade_prevista: '', local_origem_id: '' }
      ]
    });
  };

  // Remover insumo previsto
  const handleRemoveInsumo = (index) => {
    const novos = [...osForm.insumos_previstos];
    novos.splice(index, 1);
    setOsForm({ ...osForm, insumos_previstos: novos });
  };

  // Atualizar insumo previsto
  const handleUpdateInsumo = (index, field, value) => {
    const novos = [...osForm.insumos_previstos];
    novos[index] = { ...novos[index], [field]: value };
    
    // Enriquecer com nome do produto
    if (field === 'produto_id') {
      const prod = produtos.find(p => p.id === value);
      if (prod) {
        novos[index].produto_nome = prod.nome;
        novos[index].produto_codigo = prod.codigo;
      }
    }
    if (field === 'local_origem_id') {
      const loc = locais.find(l => l.id === value);
      if (loc) {
        novos[index].local_origem_nome = loc.nome;
      }
    }
    
    setOsForm({ ...osForm, insumos_previstos: novos });
  };

  // Adicionar membro da equipe
  const handleAddEquipe = () => {
    setOsForm({
      ...osForm,
      equipe: [...osForm.equipe, { human_id: '', funcao: '', fases: ['montagem', 'evento', 'encerramento'] }]
    });
  };

  // Remover membro da equipe
  const handleRemoveEquipe = (index) => {
    const novos = [...osForm.equipe];
    novos.splice(index, 1);
    setOsForm({ ...osForm, equipe: novos });
  };

  // Alternar fase (montagem/evento/encerramento) de um membro da equipe
  const handleToggleFase = (index, fase) => {
    const novos = [...osForm.equipe];
    const atuais = (novos[index].fases && novos[index].fases.length > 0)
      ? [...novos[index].fases]
      : ['montagem', 'evento', 'encerramento'];
    const idx = atuais.indexOf(fase);
    if (idx >= 0) {
      if (atuais.length === 1) {
        toast.error('O membro precisa de pelo menos uma fase');
        return;
      }
      atuais.splice(idx, 1);
    } else {
      atuais.push(fase);
    }
    novos[index] = { ...novos[index], fases: atuais };
    setOsForm({ ...osForm, equipe: novos });
  };

  // Atualizar membro da equipe
  const handleUpdateEquipe = (index, field, value) => {
    const novos = [...osForm.equipe];
    novos[index] = { ...novos[index], [field]: value };
    
    if (field === 'human_id') {
      const func = funcionarios.find(f => f.id === value);
      if (func) {
        novos[index].nome = func.name;
      }
    }
    
    setOsForm({ ...osForm, equipe: novos });
  };

  // Adicionar membro à equipe de uma OS existente
  const handleAdicionarMembro = async (os) => {
    if (!novoMembro.pessoa_id) {
      toast.error('Selecione uma pessoa');
      return;
    }
    
    const pessoa = funcionarios.find(f => f.id === novoMembro.pessoa_id);
    if (!pessoa) return;
    
    const membroNovo = {
      pessoa_id: novoMembro.pessoa_id,
      human_id: novoMembro.pessoa_id,
      nome: pessoa.name,
      funcao: novoMembro.funcao || pessoa.role || '',
      horario_inicio: novoMembro.horario_inicio || '',
      horario_fim: novoMembro.horario_fim || '',
      fases: ['montagem', 'evento', 'encerramento']
    };
    
    const equipeAtualizada = [...(os.equipe || []), membroNovo];
    
    try {
      await osApi.update(os.id, { equipe: equipeAtualizada });
      toast.success(`${pessoa.name} adicionado à equipe`);
      setAddingMembro(null);
      setNovoMembro({ pessoa_id: '', funcao: '', horario_inicio: '', horario_fim: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao adicionar membro');
    }
  };
  
  // Remover membro da equipe
  const handleRemoverMembro = async (os, membroIndex) => {
    const equipeAtualizada = os.equipe.filter((_, idx) => idx !== membroIndex);
    
    try {
      await osApi.update(os.id, { equipe: equipeAtualizada });
      toast.success('Membro removido da equipe');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover membro');
    }
  };

  // Abrir modal de execucao
  const handleAbrirExecutar = (os) => {
    setExecutandoOs(os);
    // Preparar insumos para ajuste
    const insumos = (os.insumos_previstos || []).map(i => ({
      ...i,
      quantidade_real: i.quantidade_prevista
    }));
    setInsumosAjustados(insumos);
    setShowExecutarModal(true);
  };

  // Executar OS com baixa
  const handleExecutarOs = async () => {
    if (!executandoOs) return;
    
    setExecutando(true);
    try {
      const ajustes = insumosAjustados.map(i => ({
        produto_id: i.produto_id,
        quantidade_real: parseFloat(i.quantidade_real),
        local_origem_id: i.local_origem_id
      }));
      
      const res = await osApi.executar(executandoOs.numero, ajustes.length > 0 ? ajustes : null);
      
      toast.success(res.data.message);
      
      if (res.data.movimentacoes?.some(m => m.aviso_negativo)) {
        toast.warning('Atenção: Alguns itens ficaram com saldo negativo!');
      }
      
      setShowExecutarModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar OS');
    } finally {
      setExecutando(false);
    }
  };

  // Reverter execucao
  const handleReverterOs = async () => {
    if (!revertendoOs) return;
    
    setLoading(true);
    try {
      await osApi.reverterExecucao(revertendoOs.numero);
      toast.success('Execucao revertida com sucesso');
      setShowReverterDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reverter');
    } finally {
      setLoading(false);
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-stone-100 mb-2">
          Relação de OSs
        </h1>
        <p className="text-stone-400">
          Visão geral das ordens de serviço com equipe, local e datas
        </p>
      </div>

      {/* Filtros e Acoes */}
      <Card className="bg-stone-900/50 border-stone-800 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  placeholder="Buscar por numero ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-stone-950 border-stone-800 text-stone-100"
                  data-testid="search-os"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-stone-950 border-stone-800 text-stone-100" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700">
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(statusConfig).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleParsePdf}
              />
              <Button 
                onClick={() => fileInputRef.current?.click()} 
                variant="outline"
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
                disabled={importandoPdf}
              >
                {importandoPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Importar PDF
              </Button>
              <Button onClick={handleNovaOs} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-nova-os">
                <Plus className="w-4 h-4 mr-2" /> Nova OS
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de OS */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : ordensFiltradas.length === 0 ? (
        <Card className="bg-stone-900/50 border-stone-800">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <p className="text-stone-400">Nenhuma OS encontrada</p>
            <Button onClick={handleNovaOs} variant="link" className="text-amber-400 mt-2">
              Criar primeira OS
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {ordensFiltradas.map(os => {
            const isExpanded = expandedOs === os.numero;
            const cfg = statusConfig[os.status] || statusConfig.aberta;
            
            // Formatar período
            const periodoTexto = os.periodo_completo || 
              (os.data_evento_fim && os.data_evento_fim !== os.data_evento
                ? `${formatDate(os.data_evento)} a ${formatDate(os.data_evento_fim)}`
                : formatDate(os.data_evento));
            
            return (
              <Card key={os.id} className="bg-stone-900/50 border-stone-800 overflow-hidden">
                {/* Header da OS - Linha Principal */}
                <div 
                  className="p-4 cursor-pointer hover:bg-stone-800/30 transition-colors"
                  onClick={() => setExpandedOs(isExpanded ? null : os.numero)}
                >
                  {/* Primeira linha: OS, Status e Ações */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
                      <span className="font-bold text-amber-400 text-lg">#{os.numero}</span>
                      <Badge className={`${cfg.color} border text-xs`}>{cfg.label}</Badge>
                      <span className="text-stone-300 font-medium">{os.cliente}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {os.status === 'aberta' && os.insumos_previstos?.length > 0 && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleAbrirExecutar(os); }}
                          className="bg-green-600 hover:bg-green-500 text-white h-7 text-xs"
                          data-testid={`btn-executar-${os.numero}`}
                        >
                          <Play className="w-3 h-3 mr-1" /> Executar
                        </Button>
                      )}
                      {os.status === 'executada' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); setRevertendoOs(os); setShowReverterDialog(true); }}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 h-7 text-xs"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Reverter
                        </Button>
                      )}
                      {os.status !== 'executada' && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleEditarOs(os); }}
                          className="text-stone-400 hover:text-stone-100 h-7"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Segunda linha: Colunas de dados */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm border-t border-stone-800 pt-3">
                    {/* Local */}
                    <div className="space-y-1">
                      <span className="text-stone-500 text-xs">Local</span>
                      <p className="text-stone-200 truncate" title={os.local || '-'}>
                        {os.local ? (os.local.length > 25 ? os.local.substring(0, 25) + '...' : os.local) : '-'}
                      </p>
                    </div>
                    
                    {/* Montagem */}
                    <div className="space-y-1">
                      <span className="text-orange-400 text-xs">🟠 Montagem</span>
                      <p className="text-orange-300 font-medium">
                        {os.data_montagem ? formatDate(os.data_montagem) : '-'}
                      </p>
                    </div>
                    
                    {/* Período do Evento */}
                    <div className="space-y-1">
                      <span className="text-emerald-400 text-xs">🟢 Período Evento</span>
                      <p className="text-emerald-300 font-medium">
                        {periodoTexto}
                      </p>
                    </div>
                    
                    {/* Equipe Escalada */}
                    <div className="space-y-1">
                      <span className="text-blue-400 text-xs">👥 Equipe</span>
                      <p className="text-blue-300 truncate">
                        {os.equipe?.length > 0 
                          ? os.equipe.slice(0, 3).map(e => e.nome?.split(' ')[0]).join(', ') + (os.equipe.length > 3 ? ` +${os.equipe.length - 3}` : '')
                          : '-'}
                      </p>
                    </div>
                    
                    {/* Observações */}
                    <div className="space-y-1">
                      <span className="text-stone-500 text-xs">Obs</span>
                      <p className="text-stone-400 truncate text-xs" title={os.observacoes || '-'}>
                        {os.observacoes ? (os.observacoes.length > 30 ? os.observacoes.substring(0, 30) + '...' : os.observacoes) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Detalhes expandidos com Tabs */}
                {isExpanded && (
                  <div className="border-t border-stone-800 bg-stone-950/50">
                    <Tabs defaultValue="escala" className="w-full">
                      <TabsList className="w-full justify-start bg-stone-900 border-b border-stone-800 rounded-none h-10">
                        <TabsTrigger value="escala" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                          <Users className="w-4 h-4 mr-2" />
                          Equipe ({os.equipe?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="insumos" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                          <Package className="w-4 h-4 mr-2" />
                          Insumos ({os.insumos_previstos?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="detalhes" className="data-[state=active]:bg-stone-500/20 data-[state=active]:text-stone-300">
                          <FileText className="w-4 h-4 mr-2" />
                          Detalhes
                        </TabsTrigger>
                      </TabsList>
                      
                      {/* Aba Escala/Equipe */}
                      <TabsContent value="escala" className="p-4 mt-0">
                        <div className="space-y-4">
                          {/* Lista de membros */}
                          {os.equipe?.length > 0 ? (
                            <div className="space-y-2">
                              {os.equipe.map((membro, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-stone-900 rounded-lg p-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                      <Users className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-stone-200 font-medium">{membro.nome}</p>
                                      <div className="flex items-center gap-2 text-xs text-stone-400">
                                        {membro.funcao && <span>{membro.funcao}</span>}
                                        {membro.horario_inicio && membro.horario_fim && (
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {membro.horario_inicio} - {membro.horario_fim}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {os.status !== 'executada' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => { e.stopPropagation(); handleRemoverMembro(os, idx); }}
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-stone-500">
                              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>Nenhuma pessoa escalada</p>
                            </div>
                          )}
                          
                          {/* Formulário para adicionar membro */}
                          {os.status !== 'executada' && (
                            <div className="border-t border-stone-800 pt-4">
                              {addingMembro === os.id ? (
                                <div className="bg-stone-900 rounded-lg p-4 space-y-3">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Select
                                      value={novoMembro.pessoa_id}
                                      onValueChange={(v) => setNovoMembro({...novoMembro, pessoa_id: v})}
                                    >
                                      <SelectTrigger className="bg-stone-950 border-stone-700">
                                        <SelectValue placeholder="Pessoa" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-stone-900 border-stone-700">
                                        {funcionarios.filter(f => !os.equipe?.some(e => e.pessoa_id === f.id)).map(f => (
                                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      placeholder="Obs"
                                      value={novoMembro.funcao}
                                      onChange={(e) => setNovoMembro({...novoMembro, funcao: e.target.value})}
                                      className="bg-stone-950 border-stone-700"
                                    />
                                    <Input
                                      type="time"
                                      placeholder="Entrada"
                                      value={novoMembro.horario_inicio}
                                      onChange={(e) => setNovoMembro({...novoMembro, horario_inicio: e.target.value})}
                                      className="bg-stone-950 border-stone-700"
                                    />
                                    <Input
                                      type="time"
                                      placeholder="Saída"
                                      value={novoMembro.horario_fim}
                                      onChange={(e) => setNovoMembro({...novoMembro, horario_fim: e.target.value})}
                                      className="bg-stone-950 border-stone-700"
                                    />
                                  </div>
                                  <div className="flex gap-2 justify-end">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => { setAddingMembro(null); setNovoMembro({ pessoa_id: '', funcao: '', horario_inicio: '', horario_fim: '' }); }}
                                      className="text-stone-400"
                                    >
                                      Cancelar
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleAdicionarMembro(os)}
                                      className="bg-blue-600 hover:bg-blue-500"
                                    >
                                      Adicionar
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  onClick={() => setAddingMembro(os.id)}
                                  className="w-full border-dashed border-stone-700 text-stone-400 hover:bg-stone-800"
                                >
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Adicionar Pessoa à Equipe
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      
                      {/* Aba Insumos */}
                      <TabsContent value="insumos" className="p-4 mt-0">
                        {os.insumos_previstos?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-stone-800">
                                  <TableHead className="text-stone-400">Código</TableHead>
                                  <TableHead className="text-stone-400">Produto</TableHead>
                                  <TableHead className="text-stone-400">Local</TableHead>
                                  <TableHead className="text-stone-400 text-right">
                                    {os.status === 'executada' ? 'Baixada' : 'Prevista'}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(os.status === 'executada' ? os.insumos_executados : os.insumos_previstos)?.map((insumo, idx) => (
                                  <TableRow key={idx} className="border-stone-800">
                                    <TableCell className="text-stone-300 font-mono text-xs">{insumo.produto_codigo}</TableCell>
                                    <TableCell className="text-stone-300">{insumo.produto_nome}</TableCell>
                                    <TableCell className="text-stone-400 text-xs">{insumo.local_origem_nome || insumo.local_nome}</TableCell>
                                    <TableCell className="text-right text-stone-300 font-semibold">
                                      {insumo.quantidade_prevista || insumo.quantidade}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-6 text-stone-500">
                            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Nenhum insumo cadastrado</p>
                          </div>
                        )}
                      </TabsContent>
                      
                      {/* Aba Detalhes */}
                      <TabsContent value="detalhes" className="p-4 mt-0">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-stone-500">Cliente:</span>
                              <span className="text-stone-300">{os.cliente || '-'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-stone-500">Local:</span>
                              <span className="text-stone-300">{os.local || '-'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-stone-500">Data Fim:</span>
                              <span className="text-stone-300">{formatDate(os.prazo_execucao)}</span>
                            </div>
                          </div>
                          <div className="space-y-3">
                            {os.servicos && (
                              <div>
                                <span className="text-stone-500 text-sm block mb-1">Serviços:</span>
                                <p className="text-stone-300 bg-stone-900 rounded p-2 text-sm">{os.servicos}</p>
                              </div>
                            )}
                            {os.observacoes && (
                              <div>
                                <span className="text-stone-500 text-sm block mb-1">Observações:</span>
                                <p className="text-stone-300 bg-stone-900 rounded p-2 text-sm">{os.observacoes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Info de execução */}
                        {os.status === 'executada' && os.data_execucao && (
                          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <div className="flex items-center gap-2 text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              <span>Executada em {formatDate(os.data_execucao)} por {os.executado_por_nome}</span>
                            </div>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Criar/Editar OS */}
      <Dialog open={showOsModal} onOpenChange={setShowOsModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-stone-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOs ? 'Editar OS' : 'Nova Ordem de Servico'}</DialogTitle>
            <DialogDescription className="text-stone-400">
              Preencha os dados da OS e adicione os insumos necessarios
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Dados basicos */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Numero da OS *</Label>
                <Input
                  value={osForm.numero}
                  onChange={(e) => setOsForm({...osForm, numero: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="Ex: 2024-001"
                  disabled={!!editingOs}
                  data-testid="input-os-numero"
                />
              </div>
              <div>
                <Label className="text-stone-300">Cliente *</Label>
                <Input
                  value={osForm.cliente}
                  onChange={(e) => setOsForm({...osForm, cliente: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="Nome do cliente"
                  data-testid="input-os-cliente"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Local</Label>
                <Input
                  value={osForm.local}
                  onChange={(e) => setOsForm({...osForm, local: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="Endereco ou local"
                />
              </div>
              <div>
                <Label className="text-stone-300">Data Início do Serviço *</Label>
                <Input
                  type="date"
                  value={osForm.data_evento}
                  onChange={(e) => setOsForm({...osForm, data_evento: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  data-testid="input-os-data"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-stone-300">Data Fim do Serviço</Label>
              <Input
                type="date"
                value={osForm.data_evento_fim}
                onChange={(e) => setOsForm({...osForm, data_evento_fim: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100 max-w-xs"
              />
            </div>
            
            {/* Horários Previstos */}
            <div className="space-y-3 p-3 bg-stone-900/50 rounded-lg border border-stone-800">
              <p className="text-xs text-stone-400 uppercase font-medium">Horários Previstos</p>
              
              {/* Montagem */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-stone-400 text-sm w-20">Montagem</span>
                <Input
                  type="date"
                  value={osForm.data_montagem}
                  onChange={(e) => setOsForm({...osForm, data_montagem: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 w-36"
                  placeholder="Data"
                  data-testid="input-os-data-montagem"
                />
                <Input
                  type="time"
                  value={osForm.horario_inicio_montagem}
                  onChange={(e) => setOsForm({...osForm, horario_inicio_montagem: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 w-28"
                  placeholder="Início"
                />
                <span className="text-stone-500">às</span>
                <Input
                  type="time"
                  value={osForm.horario_fim_montagem}
                  onChange={(e) => setOsForm({...osForm, horario_fim_montagem: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 w-28"
                  placeholder="Fim"
                />
              </div>
              
              {/* Evento */}
              <div className="flex items-center gap-3">
                <span className="text-stone-400 text-sm w-20">Evento</span>
                <Input
                  type="time"
                  value={osForm.horario_inicio}
                  onChange={(e) => setOsForm({...osForm, horario_inicio: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 w-28"
                  placeholder="Início"
                />
                <span className="text-stone-500">às</span>
                <Input
                  type="time"
                  value={osForm.horario_fim}
                  onChange={(e) => setOsForm({...osForm, horario_fim: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 w-28"
                  placeholder="Fim"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-stone-300">Servicos a Executar</Label>
              <Textarea
                value={osForm.servicos}
                onChange={(e) => setOsForm({...osForm, servicos: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Descreva os servicos que serao realizados..."
                rows={3}
              />
            </div>
            
            {/* Equipe */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-stone-300">Equipe Escalada</Label>
                <Button size="sm" variant="outline" onClick={handleAddEquipe} className="border-stone-700 text-stone-300 hover:bg-stone-800">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>
              {osForm.equipe.length === 0 ? (
                <p className="text-stone-500 text-sm italic">Nenhum membro adicionado</p>
              ) : (
                <div className="space-y-2">
                  {osForm.equipe.map((membro, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Select value={membro.human_id} onValueChange={(v) => handleUpdateEquipe(idx, 'human_id', v)}>
                        <SelectTrigger className="flex-1 bg-stone-950 border-stone-800 text-stone-100">
                          <SelectValue placeholder="Selecione funcionario" />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700 max-h-60">
                          {funcionarios.map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={membro.funcao}
                        onChange={(e) => handleUpdateEquipe(idx, 'funcao', e.target.value)}
                        className="w-32 bg-stone-950 border-stone-800 text-stone-100"
                        placeholder="Obs"
                      />
                      <div className="flex gap-1">
                        {[['montagem', 'M'], ['evento', 'E'], ['encerramento', 'Enc']].map(([fase, label]) => {
                          const ativas = (membro.fases && membro.fases.length > 0) ? membro.fases : ['montagem', 'evento', 'encerramento'];
                          const on = ativas.includes(fase);
                          return (
                            <button
                              key={fase}
                              type="button"
                              data-testid={`fase-${fase}-membro-${idx}`}
                              title={`Escalado para ${fase}`}
                              onClick={() => handleToggleFase(idx, fase)}
                              className={`px-2 py-1 rounded text-[10px] font-bold border transition-colors ${on ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-stone-950 border-stone-700 text-stone-600'}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => handleRemoveEquipe(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Insumos Previstos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-stone-300">Insumos Previstos</Label>
                <Button size="sm" variant="outline" onClick={handleAddInsumo} className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Insumo
                </Button>
              </div>
              {osForm.insumos_previstos.length === 0 ? (
                <p className="text-stone-500 text-sm italic">Nenhum insumo adicionado</p>
              ) : (
                <div className="space-y-3">
                  {osForm.insumos_previstos.map((insumo, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-3 bg-stone-950 rounded-lg border border-stone-800">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Select value={insumo.produto_id} onValueChange={(v) => handleUpdateInsumo(idx, 'produto_id', v)}>
                          <SelectTrigger className="bg-stone-900 border-stone-700 text-stone-100">
                            <SelectValue placeholder="Produto" />
                          </SelectTrigger>
                          <SelectContent className="bg-stone-900 border-stone-700 max-h-60">
                            {produtos.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.codigo} - {p.nome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          value={insumo.quantidade_prevista}
                          onChange={(e) => handleUpdateInsumo(idx, 'quantidade_prevista', e.target.value)}
                          className="bg-stone-900 border-stone-700 text-stone-100"
                          placeholder="Qtd"
                        />
                        <Select value={insumo.local_origem_id} onValueChange={(v) => handleUpdateInsumo(idx, 'local_origem_id', v)}>
                          <SelectTrigger className="bg-stone-900 border-stone-700 text-stone-100">
                            <SelectValue placeholder="Local origem" />
                          </SelectTrigger>
                          <SelectContent className="bg-stone-900 border-stone-700">
                            {locais.map(l => (
                              <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => handleRemoveInsumo(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mt-1">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-stone-300">Observacoes</Label>
              <Textarea
                value={osForm.observacoes}
                onChange={(e) => setOsForm({...osForm, observacoes: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Observacoes gerais..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowOsModal(false)} className="text-stone-300">
              Cancelar
            </Button>
            <Button onClick={handleSalvarOs} disabled={loading} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-salvar-os">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingOs ? 'Salvar' : 'Criar OS')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Executar OS */}
      <Dialog open={showExecutarModal} onOpenChange={setShowExecutarModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-stone-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-400" />
              Executar OS #{executandoOs?.numero}
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Confirme as quantidades e execute para dar baixa automatica no estoque
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-300 font-medium">Atencao</p>
                  <p className="text-amber-200/80">Ao executar, os insumos serao baixados automaticamente do estoque.</p>
                </div>
              </div>
            </div>
            
            <h4 className="text-sm font-semibold text-stone-300 mb-3">Insumos para Baixa</h4>
            
            {insumosAjustados.length === 0 ? (
              <p className="text-stone-500 italic">Nenhum insumo previsto</p>
            ) : (
              <div className="space-y-3">
                {insumosAjustados.map((insumo, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-stone-950 rounded-lg border border-stone-800">
                    <div className="flex-1">
                      <p className="text-stone-100 font-medium">{insumo.produto_nome}</p>
                      <p className="text-stone-500 text-xs">{insumo.local_origem_nome}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-stone-500 text-sm">Previsto: {insumo.quantidade_prevista}</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={insumo.quantidade_real}
                        onChange={(e) => {
                          const novos = [...insumosAjustados];
                          novos[idx].quantidade_real = e.target.value;
                          setInsumosAjustados(novos);
                        }}
                        className="w-24 bg-stone-900 border-stone-700 text-stone-100 text-center"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowExecutarModal(false)} className="text-stone-300">
              Cancelar
            </Button>
            <Button 
              onClick={handleExecutarOs} 
              disabled={executando || insumosAjustados.length === 0}
              className="bg-green-600 hover:bg-green-500 text-white font-semibold"
              data-testid="btn-confirmar-execucao"
            >
              {executando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Executar e Baixar Estoque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Reversao */}
      <AlertDialog open={showReverterDialog} onOpenChange={setShowReverterDialog}>
        <AlertDialogContent className="bg-stone-900 border-stone-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-stone-100">Reverter Execucao?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Isso ira estornar todas as movimentacoes de estoque vinculadas a OS #{revertendoOs?.numero}. 
              Os materiais voltarao para os locais de origem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-stone-800 text-stone-300 border-stone-700 hover:bg-stone-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleReverterOs} className="bg-amber-500 hover:bg-amber-400 text-stone-950">
              Sim, Reverter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Modal Importar PDF */}
      <Dialog open={showImportarModal} onOpenChange={setShowImportarModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Importar OS do PDF
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Confira os dados extraídos do PDF antes de importar
            </DialogDescription>
          </DialogHeader>
          
          {pdfDados && (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              {/* Dados principais - EDITÁVEIS */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-400 text-xs">Número OS</Label>
                  <Input
                    value={pdfDados.numero || ''}
                    onChange={(e) => setPdfDados({...pdfDados, numero: e.target.value})}
                    className="bg-stone-800 border-stone-700 text-amber-400 font-bold"
                  />
                </div>
                <div>
                  <Label className="text-stone-400 text-xs">Cliente</Label>
                  <Input
                    value={pdfDados.cliente || ''}
                    onChange={(e) => setPdfDados({...pdfDados, cliente: e.target.value})}
                    className="bg-stone-800 border-stone-700 text-stone-100"
                  />
                </div>
              </div>
              
              {/* 3 DATAS ESSENCIAIS */}
              <div className="bg-stone-800/80 border border-stone-600 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-stone-300 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-amber-500" />
                  Datas Essenciais
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {/* Data Montagem */}
                  <div>
                    <Label className="text-orange-400 text-xs font-medium">🟠 Montagem</Label>
                    <Input
                      type="date"
                      value={pdfDados.data_montagem || ''}
                      onChange={(e) => setPdfDados({...pdfDados, data_montagem: e.target.value})}
                      className="bg-stone-900 border-orange-500/50 text-orange-300"
                    />
                  </div>
                  {/* Data Início Evento */}
                  <div>
                    <Label className="text-emerald-400 text-xs font-medium">🟢 Início Evento</Label>
                    <Input
                      type="date"
                      value={pdfDados.data_evento || ''}
                      onChange={(e) => setPdfDados({...pdfDados, data_evento: e.target.value})}
                      className="bg-stone-900 border-emerald-500/50 text-emerald-300"
                    />
                  </div>
                  {/* Data Fim Evento */}
                  <div>
                    <Label className="text-emerald-400 text-xs font-medium">🟢 Fim Evento</Label>
                    <Input
                      type="date"
                      value={pdfDados.data_evento_fim || pdfDados.data_evento || ''}
                      onChange={(e) => setPdfDados({...pdfDados, data_evento_fim: e.target.value})}
                      className="bg-stone-900 border-emerald-500/50 text-emerald-300"
                    />
                  </div>
                </div>
                {pdfDados.horario_montagem && (
                  <p className="text-xs text-stone-400">Horário montagem: {pdfDados.horario_montagem}</p>
                )}
              </div>
              
              {/* Nome da Ativação/Evento */}
              <div>
                <Label className="text-stone-400 text-xs">Nome do Evento/Ativação</Label>
                <Input
                  value={pdfDados.descricao_evento || ''}
                  onChange={(e) => setPdfDados({...pdfDados, descricao_evento: e.target.value})}
                  placeholder="Ex: ATIVAÇÃO INTELBRAS"
                  className="bg-stone-800 border-stone-700 text-amber-300"
                />
              </div>
              
              {/* Local */}
              <div>
                <Label className="text-stone-400 text-xs">Local</Label>
                <Input
                  value={pdfDados.local || ''}
                  onChange={(e) => setPdfDados({...pdfDados, local: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-stone-100"
                />
                {pdfDados.horario_inicio && (
                  <p className="text-stone-500 text-xs mt-1">Horário: {pdfDados.horario_inicio} às {pdfDados.horario_fim}</p>
                )}
                {pdfDados.contato && (
                  <p className="text-stone-500 text-xs mt-1">Contato: {pdfDados.contato} {pdfDados.telefone && `(${pdfDados.telefone})`}</p>
                )}
              </div>
              
              {/* Serviços - apenas exibição */}
              {pdfDados.servicos?.length > 0 && (
                <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-3">
                  <p className="text-stone-400 text-xs mb-2">Serviços ({pdfDados.servicos.length})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {pdfDados.servicos.map((s, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-stone-300">{s.descricao}</span>
                        <span className="text-amber-400">x{s.quantidade}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Peças/Equipamentos - apenas exibição */}
              {pdfDados.pecas?.length > 0 && (
                <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-3">
                  <p className="text-stone-400 text-xs mb-2">Peças/Equipamentos ({pdfDados.pecas.length})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {pdfDados.pecas.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-stone-300">{p.descricao}</span>
                        <span className="text-blue-400">x{p.quantidade}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Observações - EDITÁVEL */}
              <div>
                <Label className="text-stone-400 text-xs">Observações</Label>
                <Textarea
                  value={pdfDados.observacoes || ''}
                  onChange={(e) => setPdfDados({...pdfDados, observacoes: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-stone-300 min-h-[60px]"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => { setShowImportarModal(false); setPdfDados(null); }} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleImportarOs} disabled={importandoPdf} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold">
              {importandoPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Importar OS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OsGestaoPage;
